import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import WorldGenMap from '../components/WorldGenMap';
import WorldGenJobList from '../components/WorldGenJobList';

const SIZE_OPTIONS = [1, 2, 5, 10, 15, 20, 30, 50];

function bboxFromSize(centerLat, centerLon, sizeKm) {
  const deg = sizeKm / 111.0;
  const lonAdj = deg / Math.cos((centerLat * Math.PI) / 180);
  return {
    minLat: centerLat - deg / 2,
    maxLat: centerLat + deg / 2,
    minLon: centerLon - lonAdj / 2,
    maxLon: centerLon + lonAdj / 2,
  };
}

function bboxToString(b) {
  return `${b.minLon.toFixed(6)},${b.minLat.toFixed(6)},${b.maxLon.toFixed(6)},${b.maxLat.toFixed(6)}`;
}

function bboxSizeKm(b) {
  const latKm = Math.abs(b.maxLat - b.minLat) * 111;
  const lonKm = Math.abs(b.maxLon - b.minLon) * 111 * Math.cos((b.minLat * Math.PI) / 180);
  return Math.max(latKm, lonKm);
}

export default function WorldGenerator() {
  const [bbox, setBbox] = useState(null);
  const [sizeKm, setSizeKm] = useState(1);
  const [scale, setScale] = useState(1);
  const [terrain, setTerrain] = useState(true);
  const [worldName, setWorldName] = useState('');
  const [jobs, setJobs] = useState([]);
  const [servers, setServers] = useState([]);
  const [serviceOk, setServiceOk] = useState(null);
  const [activeJobId, setActiveJobId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [applyStatus, setApplyStatus] = useState('');
  const eventSourceRef = useRef(null);

  useEffect(() => {
    api.getServers().then(setServers).catch(() => {});
    api.worldgenHealth()
      .then(d => setServiceOk(d.ok))
      .catch(() => setServiceOk(false));
    loadJobs();
  }, []); // eslint-disable-line

  const loadJobs = useCallback(() => {
    api.worldgenJobs().then(jobs => {
      setJobs(jobs);
      // Auto-resume SSE if there's a running job and we have no active one
      setActiveJobId(prev => {
        if (!prev) {
          const running = jobs.find(j => j.status === 'running' || j.status === 'queued');
          if (running) { setGenerating(true); return running.id; }
        }
        return prev;
      });
    }).catch(() => {});
  }, []);

  // SSE progress polling for active job
  useEffect(() => {
    if (!activeJobId) return;
    if (eventSourceRef.current) eventSourceRef.current.close();

    const es = new EventSource(api.worldgenProgressUrl(activeJobId));
    eventSourceRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setJobs(prev => prev.map(j =>
          j.id === activeJobId
            ? { ...j, status: data.status, progress: data.progress, stage: data.stage,
                message: data.message, stats: data.stats, error: data.error }
            : j
        ));
        if (data.status === 'done' || data.status === 'error') {
          setGenerating(false);
          setActiveJobId(null);
          es.close();
          loadJobs();
        }
      } catch (_) {}
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      setGenerating(false);
      setActiveJobId(null);
      loadJobs();
    };
    return () => es.close();
  }, [activeJobId, loadJobs]);

  const handleBBoxChange = useCallback((newBbox) => {
    setBbox(newBbox);
    const km = bboxSizeKm(newBbox);
    const nearest = SIZE_OPTIONS.reduce((a, b) => Math.abs(b - km) < Math.abs(a - km) ? b : a);
    setSizeKm(nearest);
  }, []);

  const handleSizeChange = useCallback((km) => {
    setSizeKm(km);
    const center = bbox
      ? { lat: (bbox.minLat + bbox.maxLat) / 2, lon: (bbox.minLon + bbox.maxLon) / 2 }
      : { lat: 51.1657, lon: 10.4515 };
    setBbox(bboxFromSize(center.lat, center.lon, km));
  }, [bbox]);

  const handleGenerate = useCallback(async (forceConfirmed = false) => {
    if (!bbox) { alert('Bitte zuerst ein Gebiet auswaehlen (Shift + Maus ziehen).'); return; }
    setGenerating(true);
    try {
      const params = {
        bbox: bboxToString(bbox), scale, terrain,
        name: worldName || `OSM ${sizeKm}km`,
      };
      if (forceConfirmed) params.confirmed = true;

      const result = await api.worldgenGenerate(params);

      // Service asks for confirmation (area > 30km)
      if (result.needsConfirmation) {
        setGenerating(false);
        const ok = window.confirm(
          `Grosses Gebiet: ${result.message}\n\nTrotzdem generieren?`
        );
        if (ok) {
          handleGenerate(true);
        }
        return;
      }

      const placeholder = {
        id: result.jobId, status: 'queued', progress: 0, stage: 'queued',
        message: result.estimatedMinutes
          ? `Job gestartet (geschaetzte Dauer: ~${result.estimatedMinutes} Min.)`
          : 'Job gestartet',
        hasZip: false,
        params: { bbox: bboxToString(bbox), name: worldName || `OSM ${sizeKm}km` },
        createdAt: new Date().toISOString(),
      };
      setJobs(prev => [placeholder, ...prev]);
      setActiveJobId(result.jobId);
    } catch (err) {
      setGenerating(false);
      alert('Fehler: ' + err.message);
    }
  }, [bbox, scale, terrain, worldName, sizeKm]);

  const handleApply = useCallback(async (jobId, serverId) => {
    const serverName = servers.find(s => s.id === serverId)?.name || serverId;
    const confirmed = window.confirm(
      `Welt auf "${serverName}" anwenden?\n\n` +
      `Es wird automatisch ein Backup erstellt. ` +
      `Die bisherige Welt wird danach ersetzt.\n\n` +
      `Der Server muss gestoppt sein.`
    );
    if (!confirmed) return;

    setApplyStatus('Wird vorbereitet...');
    try {
      await api.worldgenApply(jobId, serverId, (ev) => {
        setApplyStatus(ev.message || '');
        if (ev.stage === 'error') throw new Error(ev.message);
      });
      setApplyStatus('Welt erfolgreich angewendet! Server kann jetzt gestartet werden.');
      setTimeout(() => setApplyStatus(''), 6000);
    } catch (err) {
      setApplyStatus('Fehler: ' + err.message);
    }
  }, [servers]);

  const handleDelete = useCallback(async (jobId) => {
    if (!window.confirm('Job und generierte Welt loeschen?')) return;
    await api.worldgenDeleteJob(jobId).catch(() => {});
    setJobs(prev => prev.filter(j => j.id !== jobId));
  }, []);

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Weltgenerator</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.75rem', color: serviceOk === null ? '#6b7280' : serviceOk ? '#10b981' : '#ef4444' }}>
            {serviceOk === null ? 'Service wird geprueft...' : serviceOk ? 'WorldGen-Service erreichbar' : 'WorldGen-Service nicht erreichbar'}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={loadJobs}>Aktualisieren</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, marginBottom: 24 }}>
        <div className="card" style={{ padding: 16 }}>
          <WorldGenMap bbox={bbox} onBBoxChange={handleBBoxChange} sizeKm={sizeKm} />
        </div>

        <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Konfiguration</h3>

          <div>
            <label style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'block', marginBottom: 4 }}>Weltname</label>
            <input value={worldName} onChange={e => setWorldName(e.target.value)}
              placeholder="OSM Welt"
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #374151', background: '#111827', color: '#f9fafb', boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'block', marginBottom: 6 }}>
              Gebietsgroesse: <strong style={{ color: '#f97316' }}>{sizeKm} km</strong>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SIZE_OPTIONS.map(km => (
                <button key={km} onClick={() => handleSizeChange(km)} style={{
                  padding: '4px 10px', borderRadius: 6, border: '1px solid',
                  borderColor: sizeKm === km ? '#f97316' : '#374151',
                  background: sizeKm === km ? '#f9731622' : '#1f2937',
                  color: sizeKm === km ? '#f97316' : '#d1d5db',
                  cursor: 'pointer', fontSize: '0.8rem',
                }}>{km} km</button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'block', marginBottom: 4 }}>Skalierung</label>
            <select value={scale} onChange={e => setScale(Number(e.target.value))}
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #374151', background: '#111827', color: '#f9fafb' }}>
              <option value={1}>1:1 (realistisch)</option>
              <option value={2}>1:2 (groesser)</option>
              <option value={0.5}>1:0.5 (kompakter)</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="terrain-toggle" checked={terrain}
              onChange={e => setTerrain(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="terrain-toggle" style={{ fontSize: '0.875rem', cursor: 'pointer', userSelect: 'none' }}>
              Echtes Gelaende (AWS Terrain)
            </label>
          </div>

          {bbox && (
            <div style={{ background: '#111827', borderRadius: 6, padding: '8px 10px', fontSize: '0.7rem', color: '#6b7280', wordBreak: 'break-all' }}>
              {bboxToString(bbox)}
            </div>
          )}

          <button className="btn btn-primary" onClick={() => handleGenerate(false)}
            disabled={!bbox || generating || serviceOk === false}
            style={{ marginTop: 'auto', padding: '10px 0', fontSize: '0.9rem' }}>
            {generating ? 'Wird generiert...' : 'Welt generieren'}
          </button>

          {!bbox && (
            <p style={{ color: '#6b7280', fontSize: '0.75rem', textAlign: 'center', margin: 0 }}>
              Shift + Ziehen auf der Karte
            </p>
          )}
        </div>
      </div>

      {applyStatus && (
        <div style={{
          background: applyStatus.startsWith('Fehler') ? '#7f1d1d22' : applyStatus.startsWith('Welt erfolgreich') ? '#05311422' : '#1f2937',
          border: `1px solid ${applyStatus.startsWith('Fehler') ? '#ef4444' : applyStatus.startsWith('Welt erfolgreich') ? '#10b981' : '#374151'}`,
          borderRadius: 8, padding: '10px 16px', marginBottom: 16,
          color: applyStatus.startsWith('Fehler') ? '#ef4444' : applyStatus.startsWith('Welt erfolgreich') ? '#10b981' : '#d1d5db',
          fontSize: '0.875rem',
        }}>
          {applyStatus}
        </div>
      )}

      <div className="card" style={{ padding: 16 }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem' }}>Generierungsverlauf</h3>
        <WorldGenJobList jobs={jobs} servers={servers} onApply={handleApply} onDelete={handleDelete} />
      </div>
    </div>
  );
}
